import { api, unwrap } from '@/api/client';
import type { components } from '@/api/schema';
import { setSession, type AdminSession } from './session';

export type LoginChallenge = components['schemas']['AdminLoginChallengeDto'];
export type Enrolment = components['schemas']['AdminEnrolmentDto'];
export type SessionResponse = components['schemas']['AdminSessionDto'];

/**
 * Admin sign-in, step 1 (03 §12).
 *
 * ⚠️ A correct password returns a **challenge**, never a session. TOTP is
 * mandatory with no opt-out (06 §3), so there is no branch in this file where a
 * password alone produces an access token — the type says so, and the two-step
 * shape of the UI follows from it rather than being a policy the form applies.
 */
export async function requestLoginChallenge(
  email: string,
  password: string,
): Promise<LoginChallenge> {
  return unwrap(
    await api.POST('/api/v1/admin/auth/login', {
      body: { email, password },
    }),
  );
}

/**
 * Requests a reset link (03 §12).
 *
 * ⚠️ The response is identical whether or not the address belongs to an admin,
 * and the UI must not improve on that. Admin addresses are a high-value
 * phishing target, so a caller who can tell "sent" from "no such admin" has
 * been handed the list. The success copy is deliberately conditional in tone.
 */
export async function requestPasswordReset(email: string): Promise<void> {
  unwrap(
    await api.POST('/api/v1/admin/auth/forgot-password', {
      body: { email },
    }),
  );
}

/**
 * Completes a reset with the emailed token.
 *
 * ⚠️ Takes no TOTP code, and does not sign the admin in. Resetting a password
 * leaves MFA enrolment untouched — the link arrives by email, so if a reset
 * also cleared the second factor then anyone holding the mailbox would hold the
 * whole account. They finish at the sign-in screen and still need their
 * authenticator.
 */
export async function resetPassword(token: string, password: string): Promise<void> {
  unwrap(
    await api.POST('/api/v1/admin/auth/reset-password', {
      body: { token, password },
    }),
  );
}

/** Step 2: the challenge token plus a TOTP code become a session. */
export async function verifyMfa(mfaToken: string, code: string): Promise<AdminSession> {
  const result = unwrap(
    await api.POST('/api/v1/admin/auth/mfa/verify', {
      body: { mfaToken, code },
    }),
  );
  return storeSession(result);
}

/**
 * Enrolment material for an invitation.
 *
 * ⚠️ **The secret is generated and stored by the SERVER; the client only
 * displays it.** An earlier version of this app generated it in the browser,
 * because `accept-invite` used to take a `totpSecret` from the caller. That
 * inverts the trust: anyone who intercepted an invitation could enrol a secret
 * they already controlled and hold a permanent second factor for that admin.
 * Never reintroduce a client-side secret — the QR renders whatever
 * `otpauthUri` this returns, and nothing about the enrolment originates here.
 *
 * Reading it does NOT consume the invitation, so reloading the page is safe.
 */
export async function fetchEnrolment(token: string): Promise<Enrolment> {
  return unwrap(
    await api.GET('/api/v1/admin/auth/enrolment', {
      params: { query: { token } },
    }),
  );
}

/**
 * Invite acceptance: password and TOTP enrolment in one call, so an admin
 * account can never exist in a loggable-into state without MFA.
 *
 * No `totpSecret` — see `fetchEnrolment`. The code proves the authenticator
 * holds the secret the server already stored.
 */
export async function acceptInvite(input: {
  token: string;
  password: string;
  totpCode: string;
}): Promise<AdminSession> {
  const result = unwrap(
    await api.POST('/api/v1/admin/auth/accept-invite', {
      body: input,
    }),
  );
  return storeSession(result);
}

function storeSession(result: SessionResponse): AdminSession {
  return setSession({
    accessToken: result.accessToken,
    adminId: result.adminId,
    role: result.role,
    expiresIn: result.expiresIn,
  });
}
